import * as styles from "./ColorPickerArea.css";

import { clsx } from "clsx";
import { useCallback, useRef } from "react";
import type { JSX, KeyboardEvent, PointerEvent } from "react";

import { useColorPickerContext } from "./ColorPicker.context";
import type { ColorPickerAreaProps } from "./ColorPicker.types";
import { hsvToHex } from "./colorMath";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function ColorPickerArea({
  className,
}: ColorPickerAreaProps): JSX.Element {
  const { hsv, size, disabled, setHsv } = useColorPickerContext();

  const areaRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const el = areaRef.current;

      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();
      const s = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      const v = clamp(100 - ((clientY - rect.top) / rect.height) * 100, 0, 100);

      setHsv({ s, v });
    },
    [setHsv],
  );

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (disabled) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    draggingRef.current = true;
    areaRef.current?.focus();
    updateFromPointer(event.clientX, event.clientY);
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) {
      return;
    }

    updateFromPointer(event.clientX, event.clientY);
  }

  function stopDragging(event: PointerEvent<HTMLDivElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    draggingRef.current = false;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) {
      return;
    }

    const step = event.shiftKey ? 10 : 1;

    const nextByKey: Record<string, Partial<{ s: number; v: number }>> = {
      ArrowLeft: { s: clamp(hsv.s - step, 0, 100) },
      ArrowRight: { s: clamp(hsv.s + step, 0, 100) },
      ArrowDown: { v: clamp(hsv.v - step, 0, 100) },
      ArrowUp: { v: clamp(hsv.v + step, 0, 100) },
      Home: { s: 0 },
      End: { s: 100 },
    };

    const next = nextByKey[event.key];

    if (next) {
      event.preventDefault();
      setHsv(next);
    }
  }

  const hueColor = hsvToHex({ h: hsv.h, s: 100, v: 100 });

  return (
    <div
      ref={areaRef}
      data-slot="color-picker-area"
      data-disabled={disabled || undefined}
      className={clsx(styles.area({ size }), className)}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Couleur"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(hsv.v)}
      aria-valuetext={`Saturation ${Math.round(hsv.s)}%, luminosité ${Math.round(hsv.v)}%`}
      aria-disabled={disabled || undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.gradient} style={{ backgroundColor: hueColor }} />
      <div
        className={styles.thumb}
        style={{
          left: `${hsv.s}%`,
          top: `${100 - hsv.v}%`,
          backgroundColor: hsvToHex(hsv),
        }}
      />
    </div>
  );
}
